import { useEffect } from 'react';
import { Hub } from 'aws-amplify/utils';

import { authService } from '../services/authService';
import { useAuthStore } from '../stores/authStore';

/**
 * Initialize auth state at app startup and listen for Amplify Hub auth events.
 *
 * On mount: check current session and populate the store.
 * On Hub events: react to signedIn / signedOut / tokenRefresh_failure.
 */
export function useAuthListener(): void {
  const setCurrentUser = useAuthStore((s) => s.setCurrentUser);
  const setInitializing = useAuthStore((s) => s.setInitializing);

  useEffect(() => {
    let isMounted = true;

    async function loadCurrentUser(): Promise<void> {
      const user = await authService.getCurrentUserFromSession();
      if (isMounted) {
        setCurrentUser(user);
        setInitializing(false);
      }
    }

    void loadCurrentUser();

    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signedIn': {
          const user = await authService.getCurrentUserFromSession();
          setCurrentUser(user);
          break;
        }
        case 'signedOut':
        case 'tokenRefresh_failure':
          setCurrentUser(null);
          break;
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [setCurrentUser, setInitializing]);
}
